<template>
  <b-container class="p-3" style="box-sizing: border-box; height: 100vh;">
    <b-row align-h="center" align-v="center" class="mb-3">
      <b-col md="7" lg="5">
        <form-header />
        <box class="p-5">
          <!-- <hr /> -->
          <b-form @submit.prevent="submit()">
            <b-form-group>
              <b-form-label
                slot="label"
                :label="$t('firstName')"
                for="first-name"
                :errors="errors.firstName"
              />
              <b-form-input
                v-model="user.firstName"
                size="lg"
                id="first-name"
                type="text"
                :state="errors.firstName ? false : null"
              />
            </b-form-group>
            <b-form-group>
              <b-form-label
                slot="label"
                :label="$t('lastName')"
                for="last-name"
                :errors="errors.lastName"
              />
              <b-form-input
                v-model="user.lastName"
                size="lg"
                id="last-name"
                type="text"
                :state="errors.lastName ? false : null"
              />
            </b-form-group>
            <b-form-group>
              <b-form-label
                slot="label"
                :label="$t('email')"
                for="email"
                :errors="errors.email"
              />
              <b-form-input
                v-model="user.email"
                size="lg"
                id="email"
                type="text"
                :state="errors.email ? false : null"
              />
            </b-form-group>
            <b-form-group>
              <b-form-label
                slot="label"
                :label="$t('role')"
                for="select-role"
                :errors="errors.roles"
              />
              <multi-select
                id="select-role"
                btn-label="Role"
                size="lg"
                :options="rolesSelect"
                :select-options="roles"
                v-model="user.roles"
              />
            </b-form-group>
            <b-form-group>
              <b-form-label
                slot="label"
                :label="$t('password')"
                for="password"
                :errors="errors.password"
              />
              <b-form-input
                v-model="user.password"
                size="lg"
                id="password"
                type="password"
              />
              <password-security
                :password="user.password"
                @password-security="setPasswordSecurity"
              />
            </b-form-group>
            <b-form-group class="mb-2">
              <b-form-label
                slot="label"
                :label="$t('repeatPassword')"
                for="confirm-password"
                :errors="confirmPasswordError ? $t('passNotSame') : ''"
              />
              <b-form-input
                v-model="confirmPassword"
                size="lg"
                id="confirm-password"
                type="password"
                @input="confirmPasswordError = false"
              />
            </b-form-group>
            <b-form-checkbox
              size="sm"
              id="subscribe"
              v-model="user.isSubscribed"
            >
              {{ $t('subscribe') }}
            </b-form-checkbox>
            <b-form-checkbox size="sm" v-model="acceptTerms" id="terms">
              {{ $t('agreeTermsAndCond') }}
              <a href="/terms.html" target="_blank">{{ $t('read') }}</a>
            </b-form-checkbox>
            <div
              v-if="errors.acceptTerms"
              class="invalid-feedback d-block font-weight-bold"
            >
              {{ errors.acceptTerms }}
            </div>
            <b-button
              id="submit"
              style="width: 100%;"
              variant="primary"
              class="mt-4"
              :disabled="isDisabled || !strongPassword"
              type="submit"
            >
              {{ $t('signUp') }}
            </b-button>
          </b-form>
        </box>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import FormHeader from './components/FormHeader.vue';
import VueRecaptcha from 'vue-recaptcha';
import cloneDeep from 'clone';
import * as auth from '@/utils/auth.js';
import PasswordSecurity from '@/components/Forms/PasswordSecurity';

export default {
  head() {
    return {
      title() {
        return {
          inner: this.$t('metaTitle.signUp')
        };
      },
      script: [
        {
          src:
            'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit',
          defer: true,
          body: true
        }
      ]
    };
  },

  components: {
    FormHeader,
    VueRecaptcha,
    PasswordSecurity
  },

  data() {
    return {
      errors: {},
      user: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        isSubscribed: false,
        roles: []
      },
      acceptTerms: false,
      strongPassword: false,
      confirmPassword: null,
      confirmPasswordError: false,
      rolesSelect: {
        multi: true
      }
    };
  },

  computed: {
    passwordConfirm() {
      return this.user.password === this.confirmPassword;
    },
    isDisabled() {
      return !this.acceptTerms;
    },
    roles() {
      let roles = [];
      const exclude = cloneDeep(this.$config.excludedRoles);
      this.$config.roles.forEach((val, key) => {
        if (!exclude.includes(key)) {
          roles.push({ id: key, name: this.getRoleName(key) });
        }
      });
      return roles;
    }
  },

  methods: {
    getRoleName(id) {
      return this.prettify(this.$config.roles.get(id));
    },
    prettify(input) {
      if (!input) {
        return '';
      }
      let result = '';
      const words = input.split('-');
      words.forEach(word => {
        result += word.charAt(0).toUpperCase() + word.slice(1) + ' ';
      });

      return result;
    },
    submit() {
      this.errors = {};
      if (!this.passwordConfirm) {
        this.confirmPasswordError = true;
      } else {
        const user = cloneDeep(this.user);
        user.roles = user.roles.map(role => role.id);
        this.registerUser(user);
      }
    },
    async registerUser(userData) {
      try {
        const res = await this.$api.auth.post('/auth/register', {
          user: userData,
          acceptTerms: this.acceptTerms
        });

        this.$api.updateCredentials({
          token: res.accessToken,
          refreshToken: res.refreshToken,
          email: userData.email
        });

        await this.$store.dispatch('fetchUser');

        if (!auth.readActiveRole() && auth.readToken()) {
          this.$store.dispatch(
            'switchRole',
            this.$store.state.app.user.roles[0]
          );
        } else {
          this.$store.dispatch('switchRole', auth.readActiveRole());
        }

        if (auth.readActiveRole() === 'admin') {
          this.$router.replace({
            name: 'users'
          });
        } else if (auth.readActiveRole() === 'bounty-admin') {
          this.$router.replace({
            name: 'campaigns'
          });
        } else {
          this.$router.replace({
            name: 'dashboard'
          });
        }
      } catch (e) {
        this.errors = {};

        if (e.response.data.error.errors) {
          this.errors = e.response.data.error
            ? this.$parseAuthErrors(e.response.data.error.errors)
            : {};
        } else {
          e.response.data.error.message &&
            this.$notify('alert', {
              text: e.response.data.error.message,
              variant: 'danger',
              time: 5000
            });
        }
      }
    },
    setPasswordSecurity({ isSecure }) {
      this.strongPassword = isSecure;
    }
  }
};
</script>

<style lang="scss" scoped>
.row {
  min-height: 100%;
}
.logo {
  border-bottom: none;
}
</style>
